<script setup lang="ts">
import { ChevronRight, Coins, Cpu, History, Sparkles, Terminal, X } from "@lucide/vue";
import { refDebounced } from "@vueuse/core";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuditEvents } from "@/api/audit";
import { useMyCalls } from "@/api/calls";
import type { AuditEvent, ToolCall, ToolCallStatus } from "@/api/contracts";
import { useMyTools } from "@/api/me";
import AuditEventSheet from "@/components/audit/AuditEventSheet.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs from "@/components/common/PageTabs.vue";
import StatCard from "@/components/common/StatCard.vue";
import AuditItem from "@/components/items/AuditItem.vue";
import CallItem from "@/components/items/CallItem.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { formatCompact, formatDate, formatNumber, formatUsd } from "@/lib/format";
import { useAiUsage } from "@/api/admin";
import BarList from "@/components/charts/BarList.vue";

definePage({ meta: { title: "My activity" } });

const route = useRoute();
const router = useRouter();
const { me } = useSession();
const aiUsage = useAiUsage(30, "me");
const usage = computed(() => (aiUsage.data.value && aiUsage.data.value.totals.costUsd > 0 ? aiUsage.data.value : null));
const topTask = computed(() => [...(usage.value?.byTask ?? [])].sort((a, b) => b.costUsd - a.costUsd)[0]);

/** "Calls": the call log (every tool call, 14 days). "Changes": your audit events. */
const tab = computed({
  get: () => (route.query.tab === "changes" ? "changes" : "calls"),
  set: (value: string) => void router.replace({ query: { ...route.query, tab: value === "changes" ? "changes" : undefined } }),
});
const tabs = [
  { id: "calls", label: t.activity.calls, icon: Terminal },
  { id: "changes", label: t.activity.changes, icon: History },
];

/** Group by calendar day for a calm timeline. */
function byDay<T>(items: T[], at: (item: T) => string): [string, T[]][] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = formatDate(at(item));
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }
  return [...map.entries()];
}

// Calls
const callStatus = ref<ToolCallStatus | undefined>();
const callTool = ref<string | undefined>();
const calls = useMyCalls(() => ({ status: callStatus.value, tool: callTool.value }));
const callItems = computed(() => calls.data.value?.pages.flatMap((p) => p.items) ?? []);
const callDays = computed(() => byDay<ToolCall>(callItems.value, (c) => c.createdAt));
const tools = useMyTools();
const myTools = computed(() => new Set((tools.data.value ?? []).map((x) => x.name)));
const callFiltered = computed(() => Boolean(callStatus.value || callTool.value));
const callStatusOptions = [
  { value: "ok" as const, label: "Succeeded" },
  { value: "error" as const, label: "Failed" },
  { value: "denied" as const, label: "Denied" },
];

// Changes (audit events)
const q = ref("");
const debounced = refDebounced(q, 300);
const status = ref<"success" | "error" | "denied" | undefined>();
const events = useAuditEvents(() => ({ principalId: me.value?.id, q: debounced.value || undefined, status: status.value }));
const items = computed(() => events.data.value?.pages.flatMap((p) => p.items) ?? []);
const days = computed(() => byDay<AuditEvent>(items.value, (e) => e.occurredAt));

const selected = ref<AuditEvent | null>(null);
const open = ref(false);
function show(e: AuditEvent) {
  selected.value = e;
  open.value = true;
}

const statusOptions = [
  { value: "success" as const, label: "Succeeded" },
  { value: "error" as const, label: "Failed" },
  { value: "denied" as const, label: "Denied" },
];
</script>

<template>
  <div>
    <PageHeader :title="t.activity.title" :description="t.activity.description" />

    <section v-if="usage" aria-label="Your AI usage, last 30 days" class="mb-6 flex flex-col gap-3">
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard label="AI spend" :value="formatUsd(usage.totals.costUsd)" hint="Last 30 days" :icon="Coins" tone="info" />
        <StatCard label="Tokens" :value="formatCompact(usage.totals.inputTokens + usage.totals.outputTokens)" :hint="`${formatCompact(usage.totals.inputTokens)} in · ${formatCompact(usage.totals.outputTokens)} out`" :icon="Cpu" />
        <StatCard label="AI calls" :value="formatNumber(usage.totals.calls)" :hint="topTask ? `Most spent on ${topTask.task}` : undefined" :icon="Sparkles" class="hidden sm:flex" />
      </div>
      <details v-if="usage.byTask.length" class="group surface overflow-hidden">
        <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium transition-colors hover:bg-state-hover [&::-webkit-details-marker]:hidden">
          <ChevronRight class="size-4 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
          Spend by task
        </summary>
        <div class="border-t px-4 py-4">
          <BarList :items="usage.byTask.map((x) => ({ label: x.task, value: x.costUsd, note: `${formatNumber(x.calls)} calls` }))" :format="formatUsd" />
        </div>
      </details>
    </section>

    <PageTabs v-model="tab" :items="tabs" variant="segmented" label="Activity" id-prefix="activity-tab" class="mb-5" />

    <div v-if="tab === 'calls'" id="activity-panel-calls" role="tabpanel" aria-labelledby="activity-tab-calls" class="flex flex-col gap-5">
      <p class="max-w-2xl text-sm text-muted-foreground">{{ t.activity.callsHint }}</p>
      <FilterBar label="Filter calls">
        <template #chips>
          <div class="flex flex-wrap items-center gap-3">
            <FilterChips v-model="callStatus" :options="callStatusOptions" label="Status" />
            <Badge v-if="callTool" variant="outline" class="gap-1 font-mono">
              {{ t.activity.toolFilter }}: {{ callTool }}
              <button type="button" class="tap-target rounded-sm hover:text-foreground" :aria-label="`Show all tools, not only ${callTool}`" @click="callTool = undefined"><X class="size-3" /></button>
            </Badge>
          </div>
        </template>
      </FilterBar>

      <ListSkeleton v-if="calls.isPending.value" :rows="6" />
      <ErrorState v-else-if="calls.error.value" :error="calls.error.value" @retry="calls.refetch()" />
      <EmptyState v-else-if="callItems.length === 0" :icon="Terminal" :title="callFiltered ? t.activity.callsEmptyFiltered : t.activity.callsEmpty" :description="t.home.recentActivityEmpty" />
      <div v-else class="flex flex-col gap-6">
        <section v-for="[day, list] in callDays" :key="day" :aria-label="day">
          <h2 class="eyebrow mb-2">{{ day }}</h2>
          <div class="surface flex flex-col divide-y px-1">
            <CallItem v-for="c in list" :key="c.id" :call="c" :can-open="myTools.has(c.tool)" @filter-tool="callTool = $event" />
          </div>
        </section>
        <Button v-if="calls.hasNextPage.value" variant="outline" class="self-center" :loading="calls.isFetchingNextPage.value" @click="calls.fetchNextPage()">
          {{ t.common.loadMore }}
        </Button>
      </div>
    </div>

    <div v-else id="activity-panel-changes" role="tabpanel" aria-labelledby="activity-tab-changes" class="flex flex-col gap-5">
      <FilterBar v-model:search="q" placeholder="Search your activity…" label="Filter your activity">
        <template #chips><FilterChips v-model="status" :options="statusOptions" label="Status" /></template>
      </FilterBar>

      <ListSkeleton v-if="events.isPending.value" :rows="6" />
      <ErrorState v-else-if="events.error.value" :error="events.error.value" @retry="events.refetch()" />
      <EmptyState v-else-if="items.length === 0" :icon="History" :title="t.activity.empty" :description="t.home.recentActivityEmpty" />
      <div v-else class="flex flex-col gap-6">
        <section v-for="[day, list] in days" :key="day" :aria-label="day">
          <h2 class="eyebrow mb-2">{{ day }}</h2>
          <div class="surface flex flex-col divide-y px-1">
            <AuditItem v-for="e in list" :key="e.id" :event="e" interactive @open="show" />
          </div>
        </section>
        <Button v-if="events.hasNextPage.value" variant="outline" class="self-center" :loading="events.isFetchingNextPage.value" @click="events.fetchNextPage()">
          {{ t.common.loadMore }}
        </Button>
      </div>
    </div>
    <AuditEventSheet v-model:open="open" :event="selected" />
  </div>
</template>
